import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useAcknowledgePlatformAlert,
  usePlatformAlerts,
  useResolvePlatformAlert,
  type PlatformAlert,
} from '@/api/hooks/usePlatformAlerts';
import { usePlatformTenants } from '@/api/hooks/usePlatformTenants';
import type { AppError } from '@/api/client';

const PAGE_SIZE = 20;

/**
 * Cross-tenant alert triage for platform admin. Paired sibling of the
 * operator's /admin/alerts page but the filter bar replaces the
 * location / unit dropdowns with an operator dropdown — that's the
 * discriminator between "my tenant" and "every tenant".
 *
 * <p>Auto-refreshes every 30s (react-query's refetchInterval) so a
 * P1 alert landing on one operator is visible here without a manual
 * reload.
 */
export default function PlatformAlertsPage() {
  const [page, setPage] = useState(0);
  const [priority, setPriority] = useState<string>('');
  const [status, setStatus] = useState<string>('ACTIVE');
  const [tenantId, setTenantId] = useState<string>('');

  const params = useMemo(
    () => ({
      page,
      size: PAGE_SIZE,
      priority: priority || undefined,
      status: status || undefined,
      tenantId: tenantId || undefined,
      sortBy: 'createdAt' as const,
      sortDir: 'desc' as const,
    }),
    [page, priority, status, tenantId],
  );

  const { data, isLoading, isError } = usePlatformAlerts(params);
  const tenantsQuery = usePlatformTenants({
    page: 0,
    size: 100,
    sortBy: 'name',
    sortDir: 'asc',
  });

  const ack = useAcknowledgePlatformAlert();
  const resolve = useResolvePlatformAlert();

  const alerts = data?.content ?? [];
  const total = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const handleAck = (a: PlatformAlert) => {
    ack.mutate(a.id, {
      onSuccess: () => toast.success('Alert acknowledged'),
      onError: (err) => toast.error((err as unknown as AppError)?.message ?? 'Failed to acknowledge'),
    });
  };

  const handleResolve = (a: PlatformAlert) => {
    resolve.mutate(a.id, {
      onSuccess: () => toast.success('Alert resolved'),
      onError: (err) => toast.error((err as unknown as AppError)?.message ?? 'Failed to resolve'),
    });
  };

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <header>
        <p className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-400">
          Platform Admin
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-neutral-900 md:text-3xl">
          Platform Alerts
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Escalated issues across all operators. Refreshes every 30 seconds.
        </p>
      </header>

      <section className="mt-6 flex flex-wrap items-end gap-3 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
        <FilterSelect
          label="Priority"
          value={priority}
          onChange={(v) => {
            setPriority(v);
            setPage(0);
          }}
          options={[
            { value: '', label: 'All' },
            { value: 'P1', label: 'P1 — Critical' },
            { value: 'P2', label: 'P2 — High' },
            { value: 'P3', label: 'P3 — Medium' },
            { value: 'P4', label: 'P4 — Low' },
          ]}
        />
        <FilterSelect
          label="Status"
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(0);
          }}
          options={[
            { value: '', label: 'All' },
            { value: 'ACTIVE', label: 'Active' },
            { value: 'ACKNOWLEDGED', label: 'Acknowledged' },
            { value: 'RESOLVED', label: 'Resolved' },
          ]}
        />
        <FilterSelect
          label="Operator"
          value={tenantId}
          onChange={(v) => {
            setTenantId(v);
            setPage(0);
          }}
          options={[
            { value: '', label: 'All operators' },
            ...(tenantsQuery.data?.tenants ?? []).map((t) => ({
              value: t.id,
              label: t.name,
            })),
          ]}
        />
        <p className="ml-auto text-sm text-neutral-500">
          {total} {total === 1 ? 'alert' : 'alerts'}
        </p>
      </section>

      <div className="mt-4 overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
        {isLoading && (
          <div className="p-10 text-center text-sm text-neutral-400">Loading alerts…</div>
        )}
        {isError && !isLoading && (
          <div className="p-10 text-center text-sm text-red-600">Failed to load alerts.</div>
        )}
        {!isLoading && !isError && alerts.length === 0 && (
          <div className="px-6 py-16 text-center">
            <p className="text-sm font-medium text-neutral-700">No alerts</p>
            <p className="mt-1 text-sm text-neutral-500">
              All systems operating normally across all operators.
            </p>
          </div>
        )}
        {alerts.length > 0 && (
          <ul className="divide-y divide-neutral-100">
            {alerts.map((a) => (
              <AlertRow
                key={a.id}
                alert={a}
                onAck={() => handleAck(a)}
                onResolve={() => handleResolve(a)}
                busy={(ack.isPending || resolve.isPending)}
              />
            ))}
          </ul>
        )}
        {total > 0 && (
          <div className="flex items-center justify-between border-t border-neutral-100 px-4 py-3 text-xs text-neutral-500">
            <p>
              Page {page + 1} of {Math.max(1, totalPages)}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded-md border border-neutral-200 px-3 py-1.5 font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={page + 1 >= totalPages}
                className="rounded-md border border-neutral-200 px-3 py-1.5 font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AlertRow({
  alert,
  onAck,
  onResolve,
  busy,
}: {
  alert: PlatformAlert;
  onAck: () => void;
  onResolve: () => void;
  busy: boolean;
}) {
  const resolved = alert.status === 'RESOLVED';

  return (
    <li
      className={
        'flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-start sm:justify-between ' +
        (resolved ? 'opacity-60' : '')
      }
    >
      <div className="flex flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <PriorityBadge priority={alert.priority} />
          <StatusBadge status={alert.status} />
          <span className="font-mono text-xs text-neutral-500">{alert.type}</span>
          {alert.escalationLevel > 0 && (
            <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
              Escalated ×{alert.escalationLevel}
            </span>
          )}
        </div>
        <p className="text-sm font-medium text-neutral-900">{alert.title}</p>
        {alert.description && (
          <p className="text-sm text-neutral-600">{alert.description}</p>
        )}
        <p className="text-xs text-neutral-500">
          {alert.tenantName ? (
            <Link to={`/platform/tenants/${alert.tenantId}`} className="hover:underline">
              {alert.tenantName}
            </Link>
          ) : (
            <span className="font-mono">{alert.tenantId.slice(0, 8)}</span>
          )}
          {alert.locationName && <> — {alert.locationName}</>}
          {alert.unitName && <> — {alert.unitName}</>}
          <span className="mx-2">·</span>
          <RelativeTime iso={alert.createdAt} />
          {alert.acknowledgedAt && (
            <>
              <span className="mx-2">·</span>
              Ack'd <RelativeTime iso={alert.acknowledgedAt} />
            </>
          )}
          {alert.resolvedAt && (
            <>
              <span className="mx-2">·</span>
              Resolved <RelativeTime iso={alert.resolvedAt} />
            </>
          )}
        </p>
      </div>
      <div className="flex shrink-0 gap-2">
        {alert.status === 'ACTIVE' && (
          <button
            type="button"
            onClick={onAck}
            disabled={busy}
            className="inline-flex items-center rounded-md border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Acknowledge
          </button>
        )}
        {(alert.status === 'ACTIVE' || alert.status === 'ACKNOWLEDGED') && (
          <button
            type="button"
            onClick={onResolve}
            disabled={busy}
            className="inline-flex items-center rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Resolve
          </button>
        )}
      </div>
    </li>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className="flex flex-col text-xs font-medium text-neutral-500">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-700 focus-visible:border-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]/30"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const styles: Record<string, string> = {
    P1: 'bg-red-100 text-red-800 border-red-200',
    P2: 'bg-orange-100 text-orange-800 border-orange-200',
    P3: 'bg-amber-100 text-amber-800 border-amber-200',
    P4: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  };
  const cls = styles[priority] ?? 'bg-neutral-100 text-neutral-700 border-neutral-200';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${cls}`}>
      {priority}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    ACTIVE: 'bg-red-50 text-red-700 border-red-200',
    ACKNOWLEDGED: 'bg-amber-50 text-amber-700 border-amber-200',
    RESOLVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  const cls = styles[status] ?? 'bg-neutral-100 text-neutral-700 border-neutral-200';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

function RelativeTime({ iso }: { iso: string }) {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  let label: string;
  if (diffMin < 1) label = 'just now';
  else if (diffMin < 60) label = `${diffMin} min ago`;
  else if (diffHr < 24) label = `${diffHr} hr ago`;
  else if (diffDay < 30) label = `${diffDay} days ago`;
  else label = `${Math.floor(diffDay / 30)} mo ago`;
  return <span title={d.toISOString().slice(0, 16).replace('T', ' ')}>{label}</span>;
}
